import React, { useState } from "react";
import RemainingDaysBadge from "./common/RemainingDaysBadge";
import AdExtensionModal from "./AdExtensionModal";
import SimpleKeywordEditModal from "./KeywordEditModal";
import QuantityIncreaseModal from "./QuantityIncreaseModal";
import { rejectAdAPI } from "../../../../../api/index";
import { useAuth } from "../../../../../hooks/useAuth";
import "../AdDetailModal.css";

const AppReviewAdDetail = ({
  isOpen,
  onClose,
  adData,
  keywordData, // MyAdvertisement에서 전달받은 키워드 데이터
  priceGradeData, // MyAdvertisement에서 전달받은 가격 등급 데이터
  getUserDetailAPI, // MyAdvertisement에서 전달받은 사용자 상세 정보 API 함수
  onSave, // 키워드 수정 완료 시 호출될 콜백
}) => {
  const { user } = useAuth();
  const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);
  const [isKeywordEditModalOpen, setIsKeywordEditModalOpen] = useState(false);
  const [isQuantityIncreaseModalOpen, setIsQuantityIncreaseModalOpen] =
    useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // 1회 단가 계산 - 결제금액 ÷ 신청수량 (기존 광고의 실제 단가)
  const getUnitPrice = (adData) => {
    const totalPrice = adData.price || 0;
    const totalQuantity = adData.totalQuantity || 0;
    return totalQuantity > 0 ? Math.round(totalPrice / totalQuantity) : 0;
  };

  const unitPrices = {
    [adData.ad_id || adData.id]: getUnitPrice(adData),
  };

  // 숫자 포맷팅 함수
  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    return num.toLocaleString();
  };

  // 날짜 포맷팅 함수
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return dateString.split("T")[0].replace(/-/g, ".");
  };

  // 상태 클래스 반환 함수
  const getStatusClass = (status) => {
    switch (status) {
      case "APPROVED":
        return "status-progress";
      case "PENDING":
        return "status-pending";
      case "REJECTED":
        return "status-cancelled";
      case "REFUND":
        return "status-refund";
      case "DONE":
        return "status-ended";
      default:
        return "";
    }
  };

  // 상태 텍스트 반환 함수
  const getStatusText = (status) => {
    switch (status) {
      case "APPROVED":
        return "진행중";
      case "PENDING":
        return "승인대기중";
      case "REJECTED":
        return "거절";
      case "REFUND":
        return "환불";
      case "DONE":
        return "종료";
      default:
        return status;
    }
  };

  // URL 파싱 함수 (구글플레이/앱스토어 분리)
  const parseAppUrls = (productUrl) => {
    if (!productUrl) return { googlePlay: null, appStore: null };

    const urls = productUrl.split(":::");
    const googlePlay = urls.find((url) => url.includes("play.google.com"));
    const appStore = urls.find((url) => url.includes("apps.apple.com"));

    return { googlePlay, appStore };
  };

  // 서브키워드 가져오기 함수 (6개로 통일)
  const getSubKeywords = () => {
    // keywordData가 배열인 경우 SUB 타입 키워드들을 추출
    const subKeywords = keywordData?.filter((k) => k.type === "SUB") || [];
    const result = [];

    for (let i = 0; i < 6; i++) {
      result.push(subKeywords[i]?.name || "-");
    }

    return result;
  };

  // 총 결제 금액 계산 함수
  const calculateTotalPrice = () => {
    const price = adData.price || 0;
    const quantity = adData.totalQuantity || 0;
    return formatNumber(price * quantity);
  };

  // 광고 취소 핸들러
  const handleCancelAd = async () => {
    if (!adData?.ad_id) {
      alert("광고 정보를 찾을 수 없습니다.");
      return;
    }

    const confirmCancel = window.confirm("정말로 이 광고를 취소하시겠습니까?");
    if (!confirmCancel) return;

    try {
      setIsCancelling(true);

      // rejectAdAPI 호출 (필요한 파라미터들 전달)
      const currentDeposit = user?.deposit || 0; // 로그인한 사용자의 현재 예치금
      const amount = adData.price || 0;
      const newDeposit = currentDeposit + amount;

      await rejectAdAPI(
        adData.ad_id,
        adData.webMemberId || adData.member_id,
        adData.missionName || adData.adName || "광고",
        newDeposit,
        amount,
        "REJECTED",
        adData.startDate,
        adData.endDate
      );

      alert("광고가 성공적으로 취소되었습니다.");
      onClose(); // 모달 닫기
      // 부모 컴포넌트에서 데이터 새로고침을 위해 onSave 호출
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error("광고 취소 실패:", error);
      alert("광고 취소에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsCancelling(false);
    }
  };

  // username은 adData에서 가져오기 (광고 소유자 정보)
  const username = adData.username || adData.member_id || "";

  // 가격 등급 데이터는 props로 받은 것 사용
  const userData = {
    price_grade_detail: {
      data: priceGradeData || [],
    },
  };

  if (!isOpen || !adData) return null;

  const { googlePlay, appStore } = parseAppUrls(adData.productUrl);

  return (
    <>
      <div className="ad-detail-modal-overlay" onClick={onClose}>
        <div className="ad-detail-modal" onClick={(e) => e.stopPropagation()}>
          {/* 모달 헤더 */}
          <div className="modal-header">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
                width: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "15px",
                  flex: 1,
                }}
              >
                {/* 1-1-1. Image */}
                <div className="thumbnail-image">
                  <img
                    src={adData.imageUrl || "/logo192.png"}
                    alt={adData.missionName || "앱 이미지"}
                    className="product-thumbnail"
                    onError={(e) => {
                      e.target.src = "/logo192.png";
                    }}
                  />
                </div>

                {/* 1-1-2. Column */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                    flex: 1,
                  }}
                >
                  {/* 1-1-2-1. Mission Name (앱명) */}
                  <div className="product-name">
                    <a
                      href={googlePlay || appStore || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="product-link"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "block",
                      }}
                      title={adData.missionName || "-"}
                    >
                      {adData.missionName || "-"}
                    </a>
                  </div>
                  {/* 1-1-2-2. Store Name (회사명) */}
                  <div
                    className="store-name"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: "14px",
                      color: "#666",
                    }}
                    title={adData.storeName || "-"}
                  >
                    {adData.storeName || "-"}
                  </div>
                </div>

                {/* 1-1-3. Remaining Days */}
                <RemainingDaysBadge
                  startDate={adData.startDate}
                  endDate={adData.endDate}
                  status={adData.status}
                />

                {/* 1-1-4. Status */}
                <span
                  className={`status-badge ${getStatusClass(adData.status)}`}
                >
                  {getStatusText(adData.status)}
                </span>
              </div>

              {/* 1-1-5. 광고연장버튼 (가장 우측에 고정) */}
              <button
                className="extension-btn"
                style={{ marginLeft: "auto" }}
                disabled={adData.status !== "APPROVED"}
                onClick={() => setIsExtensionModalOpen(true)}
              >
                광고연장
              </button>
            </div>
          </div>

          {/* 상세 내용 */}
          <div className="modal-content">
            <div className="form-grid">
              <div className="form-row">
                <label>구글플레이</label>
                <div className="form-value">
                  {googlePlay ? (
                    <a
                      href={googlePlay}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "block",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "100%",
                      }}
                      title={googlePlay}
                    >
                      {googlePlay}
                    </a>
                  ) : (
                    "-"
                  )}
                </div>
              </div>

              <div className="form-row">
                <label>앱스토어</label>
                <div className="form-value">
                  {appStore ? (
                    <a
                      href={appStore}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "block",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "100%",
                      }}
                      title={appStore}
                    >
                      {appStore}
                    </a>
                  ) : (
                    "-"
                  )}
                </div>
              </div>

              <div className="form-row">
                <label>메인키워드</label>
                <div
                  className="form-value"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={(() => {
                    const mainKeyword = keywordData?.find(
                      (k) => k.type === "MAIN"
                    );
                    return mainKeyword?.name || adData.mainKeywordName || "-";
                  })()}
                >
                  {(() => {
                    const mainKeyword = keywordData?.find(
                      (k) => k.type === "MAIN"
                    );
                    return mainKeyword?.name || adData.mainKeywordName || "-";
                  })()}
                </div>
              </div>

              <div className="form-row">
                <label>광고 시작일</label>
                <div
                  className="form-value"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={formatDate(adData.startDate)}
                >
                  {formatDate(adData.startDate)}
                </div>
              </div>

              {getSubKeywords().map((keyword, index) => (
                <div className="form-row" key={index}>
                  <label>서브키워드{index + 1}</label>
                  <div
                    className="form-value"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={keyword}
                  >
                    {keyword}
                  </div>
                </div>
              ))}

              <div className="form-row">
                <label>1회 단가</label>
                <div
                  className="form-value"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={`${formatNumber(
                    Math.round(adData.price / adData.totalQuantity)
                  )}원`}
                >
                  {formatNumber(
                    Math.round(adData.price / adData.totalQuantity)
                  )}
                  원
                </div>
              </div>

              <div className="form-row">
                <label>신청 수량</label>
                <div
                  className="form-value"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={formatNumber(adData.totalQuantity)}
                >
                  {formatNumber(adData.totalQuantity)}
                </div>
              </div>

              <div className="form-row">
                <label>결제 금액</label>
                <div
                  className="form-value"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={`${formatNumber(adData.price)}원`}
                >
                  {formatNumber(adData.price)}원
                </div>
              </div>
            </div>
          </div>

          {/* 닫기 버튼 */}
          <div className="modal-footer">
            {(adData.status === "APPROVED" || adData.status === "PENDING") && (
              <button
                className="ad-detail-edit-keyword-btn"
                onClick={() => {
                  setIsKeywordEditModalOpen(true);
                }}
              >
                키워드 수정
              </button>
            )}
            {(adData.status === "APPROVED" || adData.status === "PENDING") && (
              <button
                className="ad-detail-increase-quantity-btn"
                onClick={() => {
                  setIsQuantityIncreaseModalOpen(true);
                }}
              >
                수량 증가
              </button>
            )}
            {adData.status === "PENDING" && (
              <button
                className="ad-detail-cancel-btn"
                onClick={handleCancelAd}
                disabled={isCancelling}
                // style={{
                //   backgroundColor: "#dc3545",
                //   color: "white",
                //   border: "none",
                //   padding: "8px 16px",
                //   borderRadius: "4px",
                //   cursor: isCancelling ? "not-allowed" : "pointer",
                //   opacity: isCancelling ? 0.6 : 1,
                // }}
              >
                {isCancelling ? "취소 중..." : "등록 취소"}
              </button>
            )}
            <button className="ad-detail-close-btn" onClick={onClose}>
              닫기
            </button>
          </div>
        </div>
      </div>
      {isExtensionModalOpen && (
        <AdExtensionModal
          isOpen={isExtensionModalOpen}
          onClose={() => setIsExtensionModalOpen(false)}
          selectedAds={[adData]}
          onExtend={(result) => {
            if (result === "success") {
              // 연장 성공 시 모달 닫기
              setIsExtensionModalOpen(false);
              // 부모 컴포넌트에 연장 완료 알림 및 데이터 새로고침
              alert("광고 연장이 성공적으로 완료되었습니다!");
              // 부모 컴포넌트의 데이터 새로고침 함수 호출
              if (onSave) {
                onSave();
              }
            }
          }}
          unitPrices={unitPrices}
          username={username}
          user={userData}
          getUserDetailAPI={getUserDetailAPI}
        />
      )}

      {/* 키워드 수정 모달 */}
      {isKeywordEditModalOpen && (
        <SimpleKeywordEditModal
          isOpen={isKeywordEditModalOpen}
          onClose={() => setIsKeywordEditModalOpen(false)}
          adData={adData}
          keywordData={keywordData}
          onSave={onSave}
        />
      )}

      {/* 수량 증가 모달 */}
      {isQuantityIncreaseModalOpen && (
        <QuantityIncreaseModal
          isOpen={isQuantityIncreaseModalOpen}
          onClose={() => setIsQuantityIncreaseModalOpen(false)}
          adData={adData}
          unitPrice={getUnitPrice(adData)}
          onSave={onSave}
        />
      )}
    </>
  );
};

export default AppReviewAdDetail;
